import useMediaQuery from '@app/util/useMediaQuery'
import { Style, styled } from 'inlines'
import { ReactNode, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'

export type ModalWrapperProps = {
  children: ReactNode
  renderContainer?: HTMLElement
  style?: Style
  position?: 'center' | 'bottom-right'
}

export const ModalWrapper = ({
  children,
  renderContainer,
  style,
  position = 'center',
}: ModalWrapperProps) => {
  const container = typeof window !== 'undefined' 
    ? (renderContainer ?? document.body)
    : null
  const isMobile = useMediaQuery()
  useLayoutEffect(() => {
    // This removed scroll of main content when modal is on
    if (!container || typeof window === 'undefined') {
      return
    }

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth

    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight
    document.body.style.overflow = 'hidden'
    document.body.style.paddingRight = `${scrollbarWidth}px`

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
    }
  }, [container]) // Run effect if container changes

  if (typeof window === 'undefined') {
    return null
  }

  const getPositionStyles = (): Style => {
    if (position === 'bottom-right') {
      return {
        display: 'flex',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10,
        justifyContent: isMobile ? 'center' : 'flex-end',
        alignItems: isMobile ? 'center' : 'flex-end',
        padding: isMobile ? 0 : '1rem',
      }
    }

    return {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 10,
    }
  }

  const wrappedContent = (
    <styled.div
      style={{
        ...getPositionStyles(),
        ...style,
      }}
    >
      {children}
    </styled.div>
  )

  return container ? createPortal(wrappedContent, container) : null
}
